import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import api, { getImageUrl } from "../../services/api";
import { useQueryClient } from "@tanstack/react-query";
import Swal from "sweetalert2";

export default function ProductForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = !!id;

  const { data: product, isLoading } = useQuery({
    queryKey: ["product", id],
    queryFn: () => api.get(`/products/${id}`).then(res => res.data),
    enabled: isEdit,
  });

  const mutation = useMutation({
    mutationFn: (formData) => {
      const token = localStorage.getItem("token");
      const headers = {
        "Content-Type": "multipart/form-data",
      };
      
      if (token) {
        headers.Authorization = `Bearer ${token.trim()}`;
      }

      if (isEdit) {
        return api.put(`/products/${id}`, formData, { headers });
      } else {
        return api.post("/products", formData, { headers });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      Swal.fire({
        icon: "success",
        title: isEdit ? "Product Updated!" : "Product Created!",
        text: isEdit 
          ? "The product has been updated successfully." 
          : "The product has been created successfully.",
        confirmButtonColor: "#6366f1",
        timer: 2000,
        showConfirmButton: true,
      }).then(() => {
        navigate("/admin/products");
      });
    },
  });

  const submit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    mutation.mutate(formData);
  };

  if (isEdit && isLoading) return (
    <div className="text-center py-12 text-gray-600">
      Loading product<span className="animate-pulse">...</span>
    </div>
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-6">
      <div className="flex justify-between items-center mb-6 pb-6 border-b border-gray-200">
        <h2 className="text-2xl font-bold text-gray-900">{isEdit ? "Edit Product" : "Add New Product"}</h2>
      </div>

      <div className="max-w-2xl">
        {mutation.isError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-6">
            {mutation.error?.response?.data?.message || "Something went wrong. Please try again."}
          </div>
        )}

        <form onSubmit={submit} className="space-y-6">
          <div className="space-y-2">
            <label htmlFor="name" className="block text-sm font-semibold text-gray-700">Product Name</label>
            <input
              id="name"
              name="name"
              type="text"
              placeholder="Enter product name"
              defaultValue={product?.name}
              required={!isEdit}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all"
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="asin" className="block text-sm font-semibold text-gray-700">ASIN</label>
            <input
              id="asin"
              name="asin"
              type="text"
              placeholder="Enter Amazon ASIN"
              defaultValue={product?.asin}
              required={!isEdit}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all"
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="image" className="block text-sm font-semibold text-gray-700">Product Image</label>
            {isEdit && product?.image && (
              <div className="mt-4 mb-4">
                <span className="text-xs font-semibold text-gray-700 mb-2 block">Current Image:</span>
                <img
                  src={getImageUrl(product.image)}
                  alt={product.name}
                  className="max-w-full max-h-[300px] w-auto h-auto rounded-lg border-2 border-gray-200 object-contain block shadow-sm"
                />
              </div>
            )}
            <input
              id="image"
              name="image"
              type="file"
              accept="image/*"
              required={!isEdit}
              className="w-full px-2 py-2 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 cursor-pointer hover:border-primary hover:bg-white transition-all file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white file:cursor-pointer file:hover:bg-primary-dark"
            />
            <p className="text-xs text-gray-500 mt-2">
              {isEdit ? "Upload a new image to replace the current one (leave empty to keep current image)" : "Upload a product image (max 5MB)"}
            </p>
          </div>

          <div className="flex gap-4 mt-8 pt-6 border-t border-gray-200">
            <button
              type="submit"
              className="flex-1 bg-gradient-to-r from-primary to-secondary text-white py-3 px-6 rounded-lg font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none min-w-[120px]"
              disabled={mutation.isPending}
            >
              {mutation.isPending ? "Saving..." : isEdit ? "Update Product" : "Create Product"}
            </button>
            <button
              type="button"
              className="flex-1 bg-white border-2 border-gray-300 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-50 transition-all min-w-[120px]"
              onClick={() => navigate("/admin/products")}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
